import { useEffect } from 'react';
import uniqby from 'lodash.uniqby';
import { useCalculateSceneDate } from "./useCalculateSceneDate";

function calculatePlannedScriptLines (scenes: any, data: any) {
  const result: any = scenes?.reduce((acc: any, sceneNumber: any) => {
    acc[sceneNumber] = data.filter((d: any) => d.scene_number === sceneNumber).length;
    return acc;
  }, {})
  return result;
}

function cleanText (text:string) {
  return text
    .normalize("NFD")                    // Separa letras de acentos
    .replace(/[\u0300-\u036f]/g, "")     // Elimina los acentos
    .replace(/\s+/g, "_")                // Reemplaza espacios por _
}

function calculateDurationPerScene (data: any) {
  return data.reduce((acc: any, item: any) => {
    const { sequence, action_duration, duration_seconds } = item;

    if (!acc[sequence]) {
      acc[sequence] = {
        totalActionDuration: 0,
        maxActionDuration: 0,
      };
    }
    
    if (typeof duration_seconds === 'number') {
      acc[sequence].totalActionDuration += duration_seconds;
    }
    
    // Check if acc[sequence].maxActionDuration is a number before comparing
    if (typeof acc[sequence].maxActionDuration === 'number' && typeof action_duration === 'number') {
      // Round to two decimal places
      acc[sequence].maxActionDuration = Math.max(
        acc[sequence].maxActionDuration,
        action_duration
      );
    }

    return acc;
  }, {});
}

function calculateCoveredScriptLines (scenes: any, data: any) {
  const result: any = scenes?.reduce((acc: any, sceneNumber: any) => {
    acc[sceneNumber] = data.filter((d: any) => d.scene_number === sceneNumber && d.covered_by_videos).length;
    return acc;
  }, {})
  return result;
}

function sortBySequenceAndPlane (arr: any[]) {
  return arr.sort((a, b) => {
    const seqDiff: number = Number(a.Sequence) - Number(b.Sequence);
    if (seqDiff !== 0) return seqDiff;

    return Number(a.Plane) - Number(b.Plane);
  });
}

function getStartTimesByDay (data: any) {
  const result: any = {};
  const allStartTimes: any = [];

  data.forEach((item: any) => {
    const day = item.Day;
    const startTime = item.startTime;

    if (!result[day]) {
      result[day] = {
        firstStartTime: startTime,
        lastStartTime: startTime,
      };
    } else {
      if (startTime < result[day].firstStartTime) {
        result[day].firstStartTime = startTime;
      }
      if (startTime > result[day].lastStartTime) {
        result[day].lastStartTime = startTime;
      }
    }

    allStartTimes.push(startTime);
  });

  result['All Days'] = {
    firstStartTime: allStartTimes.sort()[0],
    lastStartTime: allStartTimes.sort().slice(-1)[0],
  };

  return result;
}

const calculatePercentagePerScene = (data: any) => {
  const sceneCoverage: any = {};
  const sceneTotalLines: any = {};
  
  data.forEach((d: any) => {
    if(!sceneCoverage[d.scene_number]) {
      sceneCoverage[d.scene_number] = 0;
      sceneTotalLines[d.scene_number] = 0;
    }
    sceneTotalLines[d.scene_number] += 1;
    if(d.covered_by_videos) {
      sceneCoverage[d.scene_number] += 1;
    }
  }); // PLANE IS SHOT

  return Object.keys(sceneCoverage)
    .filter((sceneNumber: any) => sceneNumber  && sceneNumber !== 'undefined')
    .map((sceneNumber: any) => ({
      scene: sceneNumber,
      percentage: (sceneCoverage[sceneNumber] / sceneTotalLines[sceneNumber]) * 100,
    }));
}

const selectSceneWithMaxDuration = (data: any) => {
  const sceneDurations = data.map((x: any) => ({ duration: x.action_duration, scene: x.sequence }));
  const maxSceneDurations = sceneDurations.reduce((acc: any, item: any) => {
    const scene = item.scene;
    const duration = item.duration ?? 0;
    
    if (!acc[scene] || duration > acc[scene]) {
      acc[scene] = duration;
    }
    
    return acc;
  }, {});
  
  return maxSceneDurations;
}

function totalSceneCoverage (scenesCoveredByVideos: any[]): any {
  if (!scenesCoveredByVideos || scenesCoveredByVideos.length === 0) {
    return 0;
  }
  const completedScenes = scenesCoveredByVideos.filter(scene => scene.percentage === 100).length;
  const totalScenes = scenesCoveredByVideos.length;
  
  return { totalSceneCoveragePercentage: (completedScenes / totalScenes) * 100, completedScenes };
}

export const useFetchData = (setSceneMetadata: (metadata: any) => void, setLoading: any) => {
  const { calculateSceneDate } = useCalculateSceneDate();
  const fetchClapperCatalogData = async () => {
    try {
      const response = await fetch("https://djadx63cv2.execute-api.eu-west-1.amazonaws.com/clapperCatalog");
      const rawData = await response.json();
      const projectId = document.getElementById('project-title')?.innerText || '';
      const data = rawData.filter((item: any) => item.script_id === cleanText(projectId));
      const tempShotsNumber: any = {};
      const tempTakesNumber: any = {};
      const tempDates: any = {};
      const sequencePlaneData: any = {};
      const realTakesCounter: any = {};
      const realShootingDays = uniqby(data, (d: any) => calculateSceneDate(d.uploaded_at).day)?.length || 0;

      data.forEach((d: any) => {
        if(d.sequence === undefined || d.plane === undefined) return;

        if(tempShotsNumber[d.sequence] === undefined) {
          tempShotsNumber[d.sequence] = 0;
          tempTakesNumber[d.sequence] = 0;
          tempDates[d.sequence] = [];

        } else {
          tempShotsNumber[d.sequence] += 1;
          tempTakesNumber[d.sequence] += d.take;
          tempDates[d.sequence] = [...tempDates[d.sequence], calculateSceneDate(d.uploaded_at).day];
        }

        const key = `${d.sequence}-${d.plane}`;
        if (sequencePlaneData[key] === undefined) {
          sequencePlaneData[key] = {
            sequence: d.sequence,
            plane: d.plane,
            realStart: d.uploaded_at,
            realEnd: d.uploaded_at,
          };
        } else {
          if (d.uploaded_at < sequencePlaneData[key].realStart) {
            sequencePlaneData[key].realStart = d.uploaded_at;
          }
          if (d.uploaded_at > sequencePlaneData[key].realEnd) {
            sequencePlaneData[key].realEnd = d.uploaded_at;
          }
        }
        
        if (realTakesCounter[key] === undefined) {
          realTakesCounter[key] = {
            sequence: d.sequence,
            plane: d.plane,
            takes: 0,
          };
        }
        realTakesCounter[key].takes += 1;
      });

      const realStartAndEnd = Object.values(sequencePlaneData);
      const realTakes = Object.values(realTakesCounter);

      return {
        scenesDuration: selectSceneWithMaxDuration(data),
        actionDurationsPerScene: calculateDurationPerScene(data),
        shotsNumber: tempShotsNumber,
        takesNumber: tempTakesNumber,
        dates: tempDates,
        realStartAndEnd,
        realTakes,
        realShootingDays,
        clapperCatalog: data,
      };
    } catch (error) {
      console.error("Error querying the database:", error);
      return {};
    }
  }
  
  const fetchScriptLineData = async () => {
    try {
      const response = await fetch("https://djadx63cv2.execute-api.eu-west-1.amazonaws.com/scriptLines");
      const rawData = await response.json();
      const projectId = document.getElementById('project-title')?.innerText || '';
      const data = rawData.filter((item: any) => item.script_id === cleanText(projectId));
      const scenes = uniqby(data, (d: any) => d.scene_number)?.map((d: any) => d.scene_number).filter(Boolean);
      const plannedScriptLinesPerScene = calculatePlannedScriptLines(scenes, data);
      const totalOfPlannedScriptLines = Object.values(plannedScriptLinesPerScene).reduce((s: any, c: any) => s + c, 0);
      const coveredScriptLinesPerScene = calculateCoveredScriptLines(scenes, data);
      const totalOfCoveredLines = Object.values(coveredScriptLinesPerScene).reduce((s: any, c: any) => s + c, 0);
      const scenesCoveredByVideos = calculatePercentagePerScene(data);
      const { totalSceneCoveragePercentage, completedScenes } = totalSceneCoverage(scenesCoveredByVideos);

      return {
        percentageCoveredByVideos: (data.map((d: any) => d.covered_by_videos).filter(Boolean).length / data.length) * 100,
        scenesCoveredByVideos,
        plannedScriptLinesPerScene,
        coveredScriptLinesPerScene,
        totalOfPlannedScriptLines,
        totalOfCoveredLines,
        totalSceneCoverage: totalSceneCoveragePercentage,
        completedScenes,
        scenes,
      };
    } catch (error) {
      console.error("Error querying the database:", error);
      return {};
    }
  }
  
  const fetchCallsheetData = async () => {
    try {
      const response = await fetch("https://djadx63cv2.execute-api.eu-west-1.amazonaws.com/callsheet");
      const rawData = await response.json();
      const projectId = document.getElementById('project-title')?.innerText || '';
      const data = rawData.filter((item: any) => item.script_id === cleanText(projectId));
      const shootingDays = uniqby(data, (d: any) => d.Day)?.map((d: any) => d.Day).sort();
      return {
        callsheetRawData: sortBySequenceAndPlane(data),
        shootingDays,
        shootingHours: getStartTimesByDay(data),
      };
    } catch (error) {
      console.error("Error querying the database:", error);
      return {};
    }
  }

  const processData = async () => {
    setLoading(true);
    try {
      const clapperMetadata = await fetchClapperCatalogData();
      const scriptMetadata = await fetchScriptLineData();
      const callsheetMetadata = await fetchCallsheetData();

      setSceneMetadata({
        ...clapperMetadata,
        ...scriptMetadata,
        ...callsheetMetadata,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error processing data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    processData();
  }, []);
}
